import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, ShieldCheck } from 'lucide-react';
import { useLang, useSeo } from '@/i18n/LanguageContext';
import { MaskedLines, FadeIn, Reveal } from '@/components/Reveal';
import LeafletMap from '@/components/LeafletMap';
import Marquee from '@/components/Marquee';
import SectionHead from '@/components/SectionHead';
import Testimonials from '@/components/Testimonials';
import CtaBand from '@/components/CtaBand';
import Icon from '@/components/Icon';
import CountUp from '@/components/CountUp';

export default function Home() {
  const { content, route, waLink } = useLang();
  useSeo(content.meta.title, content.meta.description);
  const c = content;

  return (
    <>
      <section className="relative overflow-hidden bg-brand-mist pt-[112px] lg:pt-[128px]" data-testid="home-hero">
        <div className="grid-dots absolute inset-0 opacity-60" />
        <div className="pointer-events-none absolute -left-32 top-20 h-80 w-80 rounded-full bg-brand-blue/15 blur-3xl" />
        <div className="container-x relative grid items-center gap-12 pb-16 pt-8 lg:grid-cols-2 lg:gap-14 lg:pb-24 lg:pt-12">
          <div>
            <FadeIn delay={0.05}>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-blue">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute h-1.5 w-1.5 animate-sonar rounded-full bg-brand-blue" />
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />
                </span>
                {c.hero.badge}
              </span>
            </FadeIn>
            <MaskedLines
              lines={c.hero.titleLines}
              className="mt-7 block font-display text-[2.6rem] font-bold leading-[1.02] tracking-tight text-brand-navy sm:text-6xl lg:text-[4.2rem]"
              lineClassName="pr-2"
            />
            <FadeIn delay={0.5}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-brand-muted sm:text-lg">{c.hero.subtitle}</p>
            </FadeIn>
            <FadeIn delay={0.62}>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={route('contact')}
                  data-testid="hero-cta-quote"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-7 py-3.5 text-sm font-semibold text-white shadow-[0_16px_36px_-14px_rgba(30,159,224,0.9)] transition hover:-translate-y-0.5 hover:bg-brand-bluedark"
                >
                  {c.hero.ctaQuote}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-whatsapp"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-line bg-white px-7 py-3.5 text-sm font-semibold text-brand-navy transition hover:-translate-y-0.5 hover:border-[#25D366] hover:text-[#128C4B]"
                >
                  <MessageCircle className="h-4 w-4 text-[#25D366]" />
                  {c.hero.ctaWa}
                </a>
              </div>
            </FadeIn>
            <FadeIn delay={0.75}>
              <p className="mt-7 flex items-center gap-2 text-[13px] text-brand-muted">
                <ShieldCheck className="h-4 w-4 text-brand-green" />
                {c.compliance.title}
              </p>
            </FadeIn>
          </div>
          <FadeIn delay={0.25}>
            <LeafletMap />
          </FadeIn>
        </div>
      </section>

      <Marquee items={c.marquee} />

      <section className="py-20 lg:py-24" data-testid="home-stats">
        <div className="container-x grid grid-cols-2 gap-10 lg:grid-cols-4">
          {c.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className="border-l-2 border-brand-blue/20 pl-5">
                <p className="font-display text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl"><CountUp value={s.value} /></p>
                <p className="mt-2 text-sm leading-snug text-brand-muted">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-brand-mist py-20 lg:py-28" data-testid="home-benefits">
        <div className="container-x">
          <SectionHead eyebrow={c.benefits.eyebrow} title={c.benefits.title} subtitle={c.benefits.subtitle} />
          <div className="mt-12 grid gap-5 lg:grid-cols-12">
            {c.benefits.items.map((b, i) => (
              <Reveal key={i} delay={i * 0.06} className={b.span}>
                <article data-testid={`benefit-card-${i}`} className="group h-full rounded-2xl border border-brand-line bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-blue/50 hover:shadow-[0_24px_48px_-24px_rgba(30,159,224,0.35)] lg:p-8">
                  <span className="inline-flex rounded-full bg-brand-ice px-3 py-1 font-mono text-[11px] font-bold tracking-wider text-brand-blue">{b.metric}</span>
                  <h3 className="mt-5 font-display text-xl font-bold text-brand-navy sm:text-2xl">{b.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-brand-muted">{b.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="home-solutions">
        <div className="container-x">
          <SectionHead eyebrow={c.solutions.eyebrow} title={c.solutions.title} subtitle={c.solutions.subtitle} />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {c.solutions.items.slice(0, 8).map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <article data-testid={`solution-card-${i}`} className="group h-full rounded-2xl border border-brand-line bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand-blue/50 hover:shadow-[0_20px_44px_-24px_rgba(30,159,224,0.35)]">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-ice text-brand-blue transition-colors group-hover:bg-brand-blue group-hover:text-white">
                    <Icon name={s.icon} className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-[17px] font-bold leading-snug text-brand-navy">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-muted">{s.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to={route('products')} data-testid="home-solutions-link" className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-brand-blue transition hover:gap-3">
              {c.solutions.cta}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28" data-testid="home-compliance">
        <div className="container-x">
          <Reveal>
            <div className="grain relative overflow-hidden rounded-3xl bg-brand-navy px-8 py-10 lg:px-12">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand-blue/25 blur-3xl" />
              <h3 className="relative font-display text-xl font-bold text-white sm:text-2xl">{c.compliance.title}</h3>
              <div className="relative mt-6 flex flex-wrap gap-2.5">
                {c.compliance.items.map((item, i) => (
                  <span key={i} className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-mono text-[12px] font-medium text-brand-cyan">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-mist py-20 lg:py-28" data-testid="home-products">
        <div className="container-x">
          <SectionHead eyebrow={c.products.eyebrow} title={c.products.title} subtitle={c.products.subtitle} />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            {c.products.items.slice(0, 4).map((p, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <article data-testid={`product-card-${i}`} className="flex h-full flex-col rounded-2xl border border-brand-line bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-blue/50 hover:shadow-[0_24px_48px_-24px_rgba(30,159,224,0.35)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-ice text-brand-blue">
                    <Icon name={p.icon} className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-brand-navy">{p.name}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-muted">{p.desc}</p>
                  <Link to={route('products')} data-testid={`product-cta-${i}`} className="mt-6 inline-flex items-center justify-center gap-1.5 rounded-full border border-brand-blue px-5 py-2.5 text-[13px] font-semibold text-brand-blue transition hover:bg-brand-blue hover:text-white">
                    {c.products.cta}
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="home-sedes">
        <div className="container-x">
          <SectionHead eyebrow={c.contact.sedesTitle} title={c.contact.sedesSubtitle} />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {c.sedes.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.07}>
                <Link to={route('contact')} data-testid={`location-card-${s.id}`} className="group block h-full rounded-2xl border border-brand-line bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-blue/50">
                  <span className="inline-flex rounded-full bg-brand-ice px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-brand-blue">{s.badge}</span>
                  <h3 className="mt-4 font-display text-xl font-bold text-brand-navy">{s.city}</h3>
                  <p className="text-sm text-brand-muted">{s.country}</p>
                  <p className="mt-3 text-[13px] leading-relaxed text-brand-muted">{s.address}</p>
                  <p className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-blue transition group-hover:gap-2.5">
                    {c.contact.labels.mapLink}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />
      <CtaBand />
    </>
  );
}